import { BottomSheet, LoadingSpinner, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGetExternalAccounts } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetExternalAccounts";
import { useGetPayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetPayoutSettings";
import { useUpdatePayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useUpdatePayoutSettings";
import {
  type PayoutSettingsChangeableSettings,
  PayoutSettingsPageContent,
} from "@src/appV2/redesign/Accounts/PayoutSettings/components/PayoutSettingsPageContent";
import { useGoToStripeExpressSignIn } from "@src/appV2/redesign/Accounts/PayoutSettings/hooks/useGoToStripeExpressSignIn";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { useCallback, useEffect, useMemo, useRef } from "react";

export interface StripeOnboardingPayoutSettingsBottomSheetProps {
  modalState: UseModalState;
}

export function StripeOnboardingPayoutSettingsBottomSheet(
  props: StripeOnboardingPayoutSettingsBottomSheetProps
) {
  const { modalState } = props;

  const {
    data: payoutSettings,
    isLoading: isPayoutSettingsLoading,
    refetch: refetchPayoutSettings,
  } = useGetPayoutSettings();
  const {
    data: externalAccountsResponse,
    isLoading: isExternalAccountsLoading,
    refetch: refetchExternalAccounts,
  } = useGetExternalAccounts();
  const { mutateAsync: updatePayoutSettings, isLoading: isSaveLoading } = useUpdatePayoutSettings({
    includeExternalAccount: true,
  });
  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();

  const draftSettings = useRef<PayoutSettingsChangeableSettings | undefined>(undefined);

  const externalAccounts = useMemo(
    () => externalAccountsResponse?.data.map((x) => x.attributes) ?? [],
    [externalAccountsResponse]
  );

  const isLoading = isPayoutSettingsLoading || isExternalAccountsLoading;

  useLogEffect(
    APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_PAYOUT_SETTINGS_VIEWED,
    {
      payoutCapability: payoutSettings?.meta.payoutCapability,
      externalAccountsCount: externalAccounts.length,
    },
    {
      enabled:
        modalState.modalIsOpen &&
        !isLoading &&
        isDefined(payoutSettings) &&
        payoutSettings?.meta.payoutCapability !== "none",
    }
  );

  const onDraftSettingsUpdated = useCallback(
    (newDraftSettings: PayoutSettingsChangeableSettings | undefined) => {
      draftSettings.current = newDraftSettings;
    },
    []
  );

  const onSave = useCallback(async () => {
    if (draftSettings.current) {
      const selectedAccount = externalAccounts.find(
        (externalAccount) =>
          externalAccount.stripeId ===
          draftSettings.current?.selectedPreferredExternalAccountStripeId
      );

      await updatePayoutSettings({
        payoutSchedule: draftSettings.current.selectedPayoutSchedule,
        preferredExternalAccountStripeId:
          draftSettings.current.selectedPreferredExternalAccountStripeId,
      });
      logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_PAYOUT_SETTINGS_SET, {
        selectedPayoutSchedule: draftSettings.current.selectedPayoutSchedule,
        selectedAccountType: selectedAccount?.type,
        isInstantEligible: selectedAccount?.supportsInstantPayouts,
      });
      draftSettings.current = undefined;
      modalState.closeModal();
    }
  }, [externalAccounts, updatePayoutSettings, modalState]);

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      return;
    }

    void refetchPayoutSettings();
    void refetchExternalAccounts();
  }, [modalState.modalIsOpen, refetchExternalAccounts, refetchPayoutSettings]);

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
    >
      {isLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}
      {!isLoading && payoutSettings && payoutSettings.meta.payoutCapability !== "none" && (
        <PayoutSettingsPageContent
          payoutSchedule={payoutSettings.attributes.payoutSchedule}
          preferredExternalAccountStripeId={
            payoutSettings.attributes.preferredExternalAccountStripeId
          }
          instantPayFee={payoutSettings.meta.instantPayoutFee}
          payoutCapability={payoutSettings.meta.payoutCapability}
          externalAccounts={externalAccounts}
          isSaveLoading={isSaveLoading}
          isGoToStripeLoading={isGoToStripeExpressSignInLoading}
          onDraftSettingsUpdated={onDraftSettingsUpdated}
          onSave={onSave}
          onGoToStripe={goToStripeExpressSignIn}
        />
      )}
    </BottomSheet>
  );
}
